/**
 * Provedor de Mocks para Desenvolvimento
 * 
 * Responsável por:
 * - Detectar modo de desenvolvimento
 * - Simular respostas quando WebView não está disponível
 * - Gerar delays realistas
 * - Facilitar desenvolvimento sem CorelDRAW aberto
 * 
 * @module DevMockProvider
 */

import { WebViewResponse } from '../../types/messages';
import { sendToTerminal } from '../../../terminalLogger';
import { LogCategory } from '@/config/logging';

/**
 * Provedor de mocks para desenvolvimento
 */
export class DevMockProvider {
  private readonly isDevelopmentMode: boolean;
  private readonly mockDelayMs = 500; // Delay padrão para simular resposta
  private requestCounter = 0;

  constructor() {
    this.isDevelopmentMode = process.env.NODE_ENV === 'development';
  }

  /**
   * Verifica se deve usar mocks
   * 
   * @returns true se deve usar mocks (desenvolvimento + sem WebView)
   */
  public shouldMock(): boolean {
    // Somente em modo desenvolvimento E sem WebView disponível
    const hasWebView = !!(window.chrome?.webview);
    return this.isDevelopmentMode && !hasWebView;
  }

  /**
   * Gera um requestId de mock
   */
  private generateMockRequestId(): string {
    this.requestCounter = (this.requestCounter + 1) % 1_000_000;
    const ts = Date.now();
    const rand = Math.random().toString(36).slice(2, 8);
    return `mock-req-${ts}-${this.requestCounter}-${rand}`;
  }

  /**
   * Simula uma resposta de sucesso
   * 
   * @param action Action que está sendo simulada
   * @param callback Callback opcional para receber resposta
   * @param customDelay Delay customizado em ms (opcional)
   * @returns RequestId simulado
   */
  public simulateResponse(
    action: string,
    callback?: (response: WebViewResponse) => void,
    customDelay?: number
  ): string {
    const requestId = this.generateMockRequestId();
    const delay = customDelay ?? this.mockDelayMs;

    sendToTerminal('🎭 [DEV_MOCK] Simulando resposta', {
      action,
      requestId,
      delayMs: delay,
      hasCallback: !!callback
    }, LogCategory.DEV);

    if (callback) {
      setTimeout(() => {
        const mockResponse: WebViewResponse = {
          status: 'ok',
          message: `Operação simulada concluída com sucesso (Mock)`,
          result: {
            mock: true,
            action,
            timestamp: Date.now()
          },
          requestId
        } as unknown as WebViewResponse;

        try {
          callback(mockResponse);
          
          sendToTerminal('✅ [DEV_MOCK] Callback executado', {
            action,
            requestId,
            success: true
          }, LogCategory.DEV);
        } catch (error) {
          sendToTerminal('❌ [DEV_MOCK] Erro no callback', {
            action,
            requestId,
            error: error instanceof Error ? error.message : String(error)
          }, LogCategory.ERROR);
        }
      }, delay);
    }

    return requestId;
  }

  /**
   * Simula uma resposta de erro
   * 
   * @param action Action que está sendo simulada
   * @param errorMessage Mensagem de erro
   * @param callback Callback opcional
   * @param customDelay Delay customizado em ms (opcional)
   * @returns RequestId simulado
   */
  public simulateError(
    action: string,
    errorMessage: string,
    callback?: (response: WebViewResponse) => void,
    customDelay?: number
  ): string {
    const requestId = this.generateMockRequestId();
    const delay = customDelay ?? this.mockDelayMs;

    sendToTerminal('🎭 [DEV_MOCK] Simulando erro', {
      action,
      requestId,
      errorMessage,
      delayMs: delay,
      hasCallback: !!callback
    }, LogCategory.DEV);

    if (callback) {
      setTimeout(() => {
        const mockResponse: WebViewResponse = {
          status: 'erro',
          message: errorMessage,
          error: {
            mock: true,
            action,
            timestamp: Date.now()
          },
          requestId
        } as unknown as WebViewResponse;

        try {
          callback(mockResponse);
          
          sendToTerminal('✅ [DEV_MOCK] Callback de erro executado', {
            action,
            requestId,
            success: true
          }, LogCategory.DEV);
        } catch (error) {
          sendToTerminal('❌ [DEV_MOCK] Erro no callback', {
            action,
            requestId,
            error: error instanceof Error ? error.message : String(error)
          }, LogCategory.ERROR);
        }
      }, delay);
    }

    return requestId;
  }

  /**
   * Simula progresso de uma operação longa
   * 
   * @param action Action que está sendo simulada
   * @param callback Callback para receber atualizações
   * @param steps Número de steps de progresso
   * @param stepDelayMs Delay entre cada step
   * @returns RequestId simulado
   */
  public simulateProgress(
    action: string,
    callback: (response: WebViewResponse) => void,
    steps: number = 5,
    stepDelayMs: number = 300
  ): string {
    const requestId = this.generateMockRequestId();

    sendToTerminal('🎭 [DEV_MOCK] Simulando progresso', {
      action,
      requestId,
      steps,
      stepDelayMs,
      totalDuration: steps * stepDelayMs
    }, LogCategory.DEV);

    // Enviar updates de progresso
    for (let i = 1; i <= steps; i++) {
      setTimeout(() => {
        const progressResponse: WebViewResponse = {
          status: 'progress_update',
          message: `Progresso simulado: ${i}/${steps}`,
          progress: {
            current: i,
            total: steps,
            percentage: Math.round((i / steps) * 100),
            mock: true
          },
          requestId
        } as unknown as WebViewResponse;

        try {
          callback(progressResponse);
          
          sendToTerminal('📊 [DEV_MOCK] Update de progresso enviado', {
            action,
            requestId,
            step: i,
            total: steps
          }, LogCategory.DEV);
        } catch (error) {
          sendToTerminal('❌ [DEV_MOCK] Erro no callback de progresso', {
            action,
            requestId,
            step: i,
            error: error instanceof Error ? error.message : String(error)
          }, LogCategory.ERROR);
        }
      }, stepDelayMs * i);
    }

    // Enviar resposta final
    setTimeout(() => {
      const finalResponse: WebViewResponse = {
        status: 'ok',
        message: 'Operação simulada concluída com sucesso',
        result: {
          mock: true,
          action,
          steps,
          timestamp: Date.now()
        },
        requestId
      } as unknown as WebViewResponse;

      try {
        callback(finalResponse);
        
        sendToTerminal('✅ [DEV_MOCK] Resposta final enviada', {
          action,
          requestId,
          steps
        }, LogCategory.DEV);
      } catch (error) {
        sendToTerminal('❌ [DEV_MOCK] Erro no callback final', {
          action,
          requestId,
          error: error instanceof Error ? error.message : String(error)
        }, LogCategory.ERROR);
      }
    }, stepDelayMs * (steps + 1));

    return requestId;
  }

  /**
   * Verifica se está em modo de desenvolvimento
   */
  public isDevelopment(): boolean {
    return this.isDevelopmentMode;
  }

  /**
   * Verifica se WebView está disponível
   */
  public isWebViewAvailable(): boolean {
    return !!(window.chrome?.webview);
  }
}

